<template>
  <div class="order-totals">
    <OrderTotalsRow
      v-for="(row, key) of mappedRows"
      :key="key"
      :label="row.label"
      :value="row.value"
      :variant="row.variant"
      :isValueVisible="!areValuesUpdating"
      v-data-test-id="`order-totals__row--${row.key}`"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { isNumber } from '@/helpers/number'
import { getFirstNumber } from '@/helpers/string'
import OrderTotalsRow, { VARIANTS } from '@/components/_shared/Order/Totals/OrderTotalsRow.vue'

export const ORDER_TOTALS_ROWS = {
  subtotal: 'subtotal',
  shipping: 'shipping',
  promotion: 'promotion',
  giftCards: 'giftCards',
  tax: 'tax',
  total: 'total',
  usedStoreCredit: 'usedStoreCredit',
  remainingStoreCredit: 'remainingStoreCredit',
}

const isConditionSatisfied = condition => condition || condition === undefined
const isRowKeyValid = key => !!ORDER_TOTALS_ROWS[key]
const isPromotionRow = key => key === ORDER_TOTALS_ROWS.promotion
const isPromotionFormatValid = row =>
  row.hasOwnProperty('code') && row.hasOwnProperty('amount') && isNumber(row.amount)
const isTaxRow = key => key === ORDER_TOTALS_ROWS.tax
const isTaxtFormatValid = row =>
  Array.isArray(row.taxLines) &&
  row.taxLines.every(
    line => line.hasOwnProperty('label') && line.hasOwnProperty('amount') && isNumber(line.amount)
  )

export default {
  name: 'OrderTotals',
  components: {
    OrderTotalsRow,
  },

  props: {
    /**
     * @prop {Array.<{key: String, value: String, [condition]: Boolean}>} rows
     */
    rows: {
      type: Array,
      required: true,
      validator(rows) {
        return rows.every(
          row =>
            isRowKeyValid(row.key) &&
            (isNumber(row.value) ||
              (isPromotionRow(row.key) && isPromotionFormatValid(row)) ||
              (isTaxRow(row.key) && isTaxtFormatValid(row)))
        )
      },
    },
    areValuesUpdating: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      rowDefinitions: {
        [ORDER_TOTALS_ROWS.subtotal]: {
          getLabel: () => this.$t('minicart.subtotal'),
          getValue: ({ value }) => this.getAmountWithCurrency(value),
        },
        [ORDER_TOTALS_ROWS.shipping]: {
          getLabel: () => this.$t('minicart.shipping'),
          getValue: ({ value }) =>
            value > 0 ? this.getAmountWithCurrency(value) : this.$t('minicart.free'),
        },
        [ORDER_TOTALS_ROWS.promotion]: {
          getLabel: ({ code }) => `${this.$t('checkout.process.promotion')} (${code})`,
          getValue: ({ amount }) => this.getAmountWithCurrency(amount),
        },
        [ORDER_TOTALS_ROWS.giftCards]: {
          getLabel: () => this.$t('pdp.product_types.gift_cards'),
          getValue: ({ value }) => this.getAmountWithCurrency(value),
        },
        [ORDER_TOTALS_ROWS.tax]: {
          getLabel: ({ label }) => this.translateTaxLabel(label),
          getValue: ({ amount }) => this.getAmountWithCurrency(amount),
        },
        [ORDER_TOTALS_ROWS.total]: {
          getLabel: () => this.$t('minicart.total'),
          getValue: ({ value }) => this.getAmountWithCurrency(value),
        },
        [ORDER_TOTALS_ROWS.usedStoreCredit]: {
          getLabel: () => this.$t('minicart.used_store_credit'),
          getValue: ({ value }) => this.getAmountWithCurrency(value),
        },
        [ORDER_TOTALS_ROWS.remainingStoreCredit]: {
          getLabel: () => this.$t('minicart.remaining_store_credit'),
          getValue: ({ value }) => this.getAmountWithCurrency(value),
        },
      },
    }
  },
  computed: {
    ...mapGetters({
      getAmountWithCurrency: 'store/getAmountWithCurrency',
    }),

    filteredRows() {
      return this.rows.filter(
        row => (!isPromotionRow(row.key) || row.code) && isConditionSatisfied(row.condition)
      )
    },

    mappedRows() {
      return this.filteredRows.reduce((mappedRows, row, i) => {
        // tax can have muliple lines which will map to multiple rows
        if (isTaxRow(row.key)) row.taxLines.forEach(line => this.add(mappedRows, line, row.key, i))
        else this.add(mappedRows, row, row.key, i)
        return mappedRows
      }, [])
    },

    hasUsedStoreCredit() {
      return this.filteredRows.filter(row => row.key === ORDER_TOTALS_ROWS.usedStoreCredit).length
    },
  },

  methods: {
    add(mappedRows, row, key, i) {
      mappedRows.push({
        label: this.getLabel(key, row),
        value: this.getValue(key, row),
        variant: this.getVariant(i, this.filteredRows),
        key,
      })
    },

    getLabel(key, data) {
      return this.rowDefinitions[key].getLabel(data)
    },

    getValue(key, data) {
      return this.rowDefinitions[key].getValue(data)
    },

    getVariant(index, rows) {
      const offset = this.hasUsedStoreCredit ? 2 : 1
      return index === rows.length - offset ? VARIANTS.bold : VARIANTS.default
    },

    translateTaxLabel(label) {
      const percentage = getFirstNumber(label)
      if (percentage) {
        return this.$t('checkout.payment.tax_label', { percentage })
      }
      return label
    },
  },
}
</script>
<style scoped lang="scss">
.order-totals {
  border: none;
  overflow: auto;
}
</style>
